// Artiklar (admin mockup §2): the registry with every code that identifies a product and
// the profile that applies to it, with the level that decided it.

import { useEffect, useMemo, useState } from "react";
import { Admin, Api, type BundleProduct, type ReferenceBundle, type StockRow } from "../api/client.js";
import { fmtQty, t, uomLabel } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";
import { buildRegistry, rulesFor } from "../receive/registry.js";

export function Products() {
  const [bundle, setBundle] = useState<ReferenceBundle | null>(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<BundleProduct | null>(null);
  useDetailHistory(!!selected, () => { setSelected(null); });
  useEffect(() => {
    Api.bundle().then(setBundle).catch(() => setBundle(null));
  }, []);
  const registry = useMemo(() => (bundle ? buildRegistry(bundle) : null), [bundle]);
  const [note, setNote] = useState<string | null>(null);
  // What of the article is in the building and where (owner 2026-11-02): the container projection for
  // this product, cartons and loose stock in received/stored/picked, grouped by place with the pallet named.
  const [stock, setStock] = useState<StockRow[] | null>(null);
  useEffect(() => {
    setStock(null);
    if (!selected) return;
    let stop = false;
    Admin.stock(selected.id).then((rows) => { if (!stop) setStock(rows); }).catch(() => { if (!stop) setStock([]); });
    return () => { stop = true; };
  }, [selected?.id]);
  const stockRows = useMemo(() => (stock ?? []).filter((r) => r.kind !== "logistic-unit" && (r.state === "received" || r.state === "stored" || r.state === "picked") && Number(r.quantity) > 0), [stock]);
  const stockSums = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of stockRows) m.set(r.uom, (m.get(r.uom) ?? 0) + Number(r.quantity));
    return [...m.entries()].map(([uom, n]) => fmtQty(String(Math.round(n * 1000) / 1000), uom)).join(" · ");
  }, [stockRows]);
  const palletOf = useMemo(() => new Map((stock ?? []).filter((r) => r.kind === "logistic-unit").map((r) => [r.container_id, r])), [stock]);
  // The article level of profiles in levels: set here, applies when the levels above allow it.
  const setArticleProfile = async (p: BundleProduct, name: string | null) => {
    setNote(null);
    try {
      await Admin.setProductProfile(p.id, name);
      const b = await Api.bundle();
      setBundle(b);
      setSelected(b.products.find((x) => x.id === p.id) ?? null);
      setNote(t("adm.pr.profileSaved"));
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    }
  };

  const list = useMemo(() => {
    if (!bundle) return [];
    const needle = q.trim().toLowerCase();
    const all = bundle.products.filter((p) => !needle || p.sku.toLowerCase().includes(needle) || p.name.toLowerCase().includes(needle) || p.gtins.some((g) => g.gtin.includes(needle)) || (p.category ?? "").toLowerCase().includes(needle));
    return all.slice(0, 200);
  }, [bundle, q]);

  const rules = selected && registry ? rulesFor(registry, selected, selected.gtins[0] ?? null) : null;
  const counted = selected?.gtins.find((g) => g.displayName)?.displayName ?? (selected?.gtins.some((g) => g.level === "case") ? t("unit.case") : t("unit.kolli"));
  const storageClass = rules?.storage.storageClass ?? null;

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.pr.title")}</h1><div className="crumb">{bundle ? t("adm.pr.sub", { total: bundle.products.length, active: bundle.products.length, gtins: bundle.products.filter((p) => p.gtins.length > 0).length }) : ""}</div></div>
        <div className="right"><input className="ain" style={{ height: 36, width: 320, borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", padding: "0 12px", font: "inherit", color: "var(--text)" }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adm.pr.search")} autoFocus /></div>
      </div>
      <div className="adm-content">
        <div className={`adm-cols${!!selected ? " detail-open" : ""}`}>
          <div className="card adm-grow">
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.pr.col.sku")}</th><th>{t("adm.pr.col.name")}</th><th>{t("adm.pr.col.category")}</th><th>{t("adm.pr.col.unit")}</th><th className="num">{t("adm.pr.col.gtin")}</th></tr></thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.id} className={`click${selected?.id === p.id ? " sel" : ""}`} onClick={() => setSelected(p)}>
                      <td className="id">{p.sku}</td>
                      <td>{p.name}</td>
                      <td className="mut">{p.category ?? "—"}</td>
                      <td className="id">{uomLabel(p.baseUom)}</td>
                      <td className="num">{p.gtins.length}</td>
                    </tr>
                  ))}
                  {bundle && bundle.products.length > list.length && <tr><td colSpan={5} className="mut">{t("adm.pr.more", { n: bundle.products.length - list.length })}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          {selected && rules && (
            <div className="adm-w460 adm-detail adm-detail-only" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {!!selected && <DetailBar title={selected ? `${selected.sku} · ${selected.name}` : ""} sub={undefined} onBack={() => { setSelected(null); }} />}
              <div className="label" style={{ background: "var(--panel)" }}>
                <div className="head"><b>{selected.name}</b><span>{selected.sku} · {selected.category ?? ""}</span></div>
                <div className="rows">
                  <div className="cell"><div className="t">{t("adm.pr.baseUom")}</div><div className="v">{uomLabel(selected.baseUom)}</div></div>
                  <div className="cell"><div className="t">{t("adm.pr.storageClass")}</div><div className="v" style={{ fontFamily: "var(--sans)", fontSize: 18 }}>{storageClass === "chilled" ? "Kyl" : storageClass === "frozen" ? "Frys" : storageClass === "ambient" ? "Omgivning" : "—"}</div></div>
                  <div className="cell"><div className="t">{t("adm.pr.counted")}</div><div className="v" style={{ fontFamily: "var(--sans)", fontSize: 18 }}>{counted}</div></div>
                  <div className="cell"><div className="t">{t("adm.pr.profile")}</div><div className="v" style={{ fontFamily: "var(--sans)", fontSize: 18 }}>{rules.profileName} <small>v{rules.profileVersion} · {t(`adm.pr.profileFrom.${rules.profileFrom}` as "adm.pr.profileFrom.site")}{rules.lockedBy ? ` · ${t(`adm.pr.lockedBy.${rules.lockedBy}` as "adm.pr.lockedBy.company")}` : ""}</small></div></div>
                  <div className="cell wide"><div className="t">{t("adm.pr.articleProfile")}</div><div className="v" style={{ fontFamily: "var(--sans)", fontSize: 14, fontWeight: 500, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <select value={selected.handlingProfileName ?? ""} disabled={rules.lockedBy !== null} onChange={(e) => void setArticleProfile(selected, e.target.value || null)}>
                      <option value="">{t("adm.pr.articleProfile.none")}</option>
                      {(bundle?.profiles ?? []).map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                    </select>
                    <small className="mut">{rules.lockedBy ? t(`adm.pr.lockedBy.${rules.lockedBy}` as "adm.pr.lockedBy.company") : t("adm.pr.articleProfile.hint")}</small>
                    {note && <small>{note}</small>}
                  </div></div>
                  <div className="cell wide"><div className="t">{t("adm.pr.storage")}</div><div className="v" style={{ fontFamily: "var(--sans)", fontSize: 14, fontWeight: 500, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <select value={selected.storageRequirement ?? ""} onChange={(e) => void (async () => { setNote(null); try { await Admin.setProductStorage(selected.id, e.target.value || null); const b = await Api.bundle(); setBundle(b); setSelected(b.products.find((x) => x.id === selected.id) ?? null); setNote(t("adm.pr.storageSaved")); } catch (err) { setNote(err instanceof Error ? err.message : String(err)); } })()}>
                      <option value="">{t("adm.pr.storage.none")}</option>
                      {(((bundle?.siteConfig as unknown as { locations?: { storageClasses?: { key: string; name: string }[] } } | null)?.locations?.storageClasses) ?? []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                    </select>
                    <small className="mut">{t("adm.pr.storage.hint")}</small>
                  </div></div>
                  <div className="cell wide"><div className="t">{t("adm.set.capture")}</div><div className="v" style={{ fontFamily: "var(--sans)", fontSize: 14, fontWeight: 500 }}>{rules.identification.scheme} · batch {rules.capture.lot} · bäst före {rules.capture.bestBefore} · {rules.capture.partialCodes}{rules.supplierOverrides.length ? ` · leverantör: ${rules.supplierOverrides.join(", ")}` : ""}</div></div>
                </div>
              </div>
              <div className="card">
                <div className="hd">{t("adm.pr.stock")} <span className="sub">{stock === null ? "…" : stockRows.length === 0 ? t("adm.pr.stock.none") : `${stockSums} · ${t("adm.pr.stock.sub", { units: stockRows.length, places: new Set(stockRows.map((r) => r.location_code ?? "")).size })}`}</span></div>
                {stockRows.length > 0 && (
                  <div className="bd flush">
                    <table className="at">
                      <thead><tr><th>{t("adm.st.location")}</th><th>{t("adm.st.unit")}</th><th>{t("adm.st.lot")}</th><th>{t("adm.st.bestBefore")}</th><th className="num">{t("adm.st.content")}</th><th>{t("adm.col.status")}</th></tr></thead>
                      <tbody>
                        {stockRows.map((r) => {
                          const pallet = r.parent_id ? palletOf.get(r.parent_id) ?? null : null;
                          const unplaced = !r.location_code || r.state === "received";
                          return (
                            <tr key={r.container_id}>
                              <td className="id">{r.location_code ?? "—"}</td>
                              <td className="mut">{pallet ? `${t("unit.pallet")} ${pallet.sscc ? `${pallet.sscc.slice(0, 4)}…${pallet.sscc.slice(-4)}` : ""}` : r.parent_id ? t("unit.pallet") : r.kind === "loose" ? t("adm.pr.stock.loose") : t("unit.kolli")}</td>
                              <td className="id">{r.lot_number ?? "—"}</td>
                              <td className="mut">{r.best_before ?? "—"}</td>
                              <td className="num">{fmtQty(r.quantity, r.uom)}</td>
                              <td className="mut">{r.state === "picked" ? t("adm.pr.stock.picked") : unplaced ? t("adm.pr.stock.unplaced") : ""}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="card">
                <div className="hd">{t("adm.pr.codes")} <span className="sub">{t("adm.pr.codes.sub")}</span></div>
                <div className="bd flush">
                  <table className="at">
                    <thead><tr><th>GTIN</th><th>{t("adm.pr.level")}</th><th>{t("adm.pr.supplier")}</th></tr></thead>
                    <tbody>
                      {selected.gtins.length === 0 && <tr><td colSpan={3} className="mut">—</td></tr>}
                      {selected.gtins.map((g) => <tr key={g.gtin}><td className="id">{g.gtin}</td><td className="mut">{g.displayName ?? g.level ?? "—"}</td><td className="mut">{g.supplierPartyId ? (bundle?.parties.find((p) => p.id === g.supplierPartyId)?.name ?? g.supplierPartyId) : "—"}{g.quantityFrom ? ` · ${t("adm.pr.contentFrom", { ai: g.quantityFrom.ai })}` : ""}</td></tr>)}
                      {selected.supplierArticles.map((a) => <tr key={a.article}><td className="id mut">{a.article}</td><td className="mut">lev.art.nr</td><td className="mut">{bundle?.parties.find((p) => p.id === a.supplierPartyId)?.name ?? ""}</td></tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
